import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertCircle, CheckCircle, Loader2, CreditCard, Key, User, RefreshCw, ExternalLink, ListOrdered, Search, XCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import renewalAPI from '../api/renewal';
import LoginModal from '../components/LoginModal';

export default function SelfServiceRenewal() {
 const { t } = useTranslation();
 const navigate = useNavigate();

 // 表单数据
 const [formData, setFormData] = useState({
 selectedOrderId: '',
 selectedCardCode: '',
 token: '',
 processOrderId: ''
 });

 // 状态管理
 const [loading, setLoading] = useState(false);
 const [error, setError] = useState('');
 const [orderList, setOrderList] = useState([]);
 const [cardList, setCardList] = useState([]);
 const [renewalStatus, setRenewalStatus] = useState(null);
 const [processing, setProcessing] = useState(false);
 const [success, setSuccess] = useState(false);
 
 // 校验状态
 const [cardVerified, setCardVerified] = useState(false);
 const [tokenVerified, setTokenVerified] = useState(false);
 const [verifyingCard, setVerifyingCard] = useState(false);
 const [verifyingToken, setVerifyingToken] = useState(false);
 const [cardError, setCardError] = useState('');
 const [tokenError, setTokenError] = useState('');

 // 轮询状态
 const [polling, setPolling] = useState(false);
 const [pollCount, setPollCount] = useState(0);
 
 // 登录弹窗状态
 const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);

 // 加载订单号列表
 const loadOrderList = async () => {
 setLoading(true);
 try {
 const response = await renewalAPI.getOrderList();
 if (response.success) {
 setOrderList(response.data);
 } else {
 // 处理未登录或其他错误情况
 setError(response.message || t('renewal.errors.loadOrdersFailed'));
 setOrderList([]);
 }
 } catch (error) {
 setError(t('renewal.errors.loadOrdersFailed'));
 } finally {
 setLoading(false);
 }
 };

 // 加载卡密列表
 const loadCardsByOrder = async (orderId) => {
 if (!orderId) {
 setCardList([]);
 return;
 }
 
 setLoading(true);
 try {
 const response = await renewalAPI.getCardsByOrder(orderId);
 if (response.success) {
 setCardList(response.data);
 // 重置卡密选择
 setFormData(prev => ({ ...prev, selectedCardCode: '' }));
 }
 } catch (error) {
 setError(t('renewal.errors.loadCardsFailed'));
 } finally {
 setLoading(false);
 }
 };

 // 监听订单选择变化
 useEffect(() => {
 if (formData.selectedOrderId) {
 loadCardsByOrder(formData.selectedOrderId);
 // 重置卡密校验状态
 setCardVerified(false);
 setCardError('');
 }
 }, [formData.selectedOrderId]);
 
 // 监听卡密选择变化
 useEffect(() => {
 if (formData.selectedCardCode && cardVerified) {
 // 如果已经校验过，但内容变化了，显示需要重新校验
 setCardVerified(false);
 setCardError('');
 }
 }, [formData.selectedCardCode]);
 
 // 监听Token变化
 useEffect(() => {
 if (formData.token && tokenVerified) {
 // 如果已经校验过，但内容变化了，显示需要重新校验
 setTokenVerified(false);
 setTokenError('');
 }
 }, [formData.token]);

 // 页面加载时获取订单列表
 useEffect(() => {
 loadOrderList();
 }, []);

 // 校验卡密
 const verifyCard = async () => {
 if (!formData.selectedCardCode) {
 setCardError(t('renewal.errors.selectCard'));
 return;
 }
 
 setVerifyingCard(true);
 setCardError('');
 
 try {
 const response = await renewalAPI.verifyCard(formData.selectedCardCode);
 
 if (response.success) {
 setCardVerified(true);
 setCardError('');
 } else {
 setCardVerified(false);
 setCardError(response.message || t('renewal.errors.cardVerificationFailed'));
 }
 } catch (error) {
 console.error('卡密验证异常:', error);
 setCardVerified(false);
 setCardError(t('renewal.errors.cardVerificationFailed'));
 } finally {
 setVerifyingCard(false);
 }
 };

 // 校验Token
 const verifyToken = async () => {
 if (!formData.token) {
 setTokenError(t('renewal.errors.emptyToken'));
 return;
 }
 
 setVerifyingToken(true);
 setTokenError('');
 
 try {
 const response = await renewalAPI.verifyToken(formData.token);
 
 if (response.success) {
 setTokenVerified(true);
 setTokenError('');
 } else {
 setTokenVerified(false);
 setTokenError(response.message || t('renewal.errors.tokenVerificationFailed'));
 }
 } catch (error) {
 setTokenVerified(false);
 setTokenError(t('renewal.errors.tokenVerificationFailed'));
 } finally {
 setVerifyingToken(false);
 }
 };

 // 处理充值
 const handleRenewal = async () => {
 // 验证所有字段
 if (!formData.selectedOrderId) {
 setError(t('renewal.errors.selectOrder'));
 return;
 }
 if (!cardVerified) {
 setError(t('renewal.errors.cardNotVerified'));
 return;
 }
 if (!tokenVerified) {
 setError(t('renewal.errors.tokenNotVerified'));
 return;
 }

 setProcessing(true);
 setError('');

 try {
 // 开始充值
 const response = await renewalAPI.processRenewal(
 formData.selectedOrderId,
 formData.selectedCardCode,
 formData.token
 );

 if (response.success) {
 setFormData({ ...formData, processOrderId: response.data.taskId });
 setRenewalStatus({
 status: 'processing',
 taskId: response.data.taskId
 });
 // 开始轮询查询充值进度
 startPolling(response.data.taskId);
 } else {
 setError(response.message || t('renewal.errors.renewalProcessFailed'));
 setProcessing(false);
 }
 } catch (error) {
 setError(t('renewal.errors.renewalProcessFailed'));
 setProcessing(false);
 }
 };

 // 轮询查询充值进度
 const startPolling = (taskId) => {
 setPolling(true);
 setPollCount(0);
 };

 // 查询充值状态
 const checkRenewalStatus = async (taskId) => {
 try {
 const response = await renewalAPI.checkStatus(taskId);
 
 if (response.success) {
 const status = response.data.status;
 setRenewalStatus(response.data);

 if (status === 'success') {
 setPolling(false);
 setProcessing(false);
 setSuccess(true);
 } else if (status === 'failed') {
 setPolling(false);
 setProcessing(false);
 setError(t('renewal.messages.renewalFailed') + ': ' + (response.data.message || t('common.error')));
 }
 }
 } catch (error) {
 console.error('查询充值状态失败', error);
 }
 };

 // 轮询逻辑
 useEffect(() => {
 if (polling && formData.processOrderId) {
 const interval = setInterval(() => {
 setPollCount(prev => prev + 1);
 checkRenewalStatus(formData.processOrderId);

 // 最多轮询60次（5分钟）
 if (pollCount >= 60) {
 setPolling(false);
 setProcessing(false);
 setError(t('renewal.errors.renewalTimeout'));
 }
 }, 5000); // 每5秒查询一次

 return () => clearInterval(interval);
 }
 }, [polling, formData.processOrderId, pollCount]);

 // 重置流程
 const resetProcess = () => {
 setFormData({
 selectedOrderId: '',
 selectedCardCode: '',
 token: '',
 processOrderId: ''
 });
 setCardList([]);
 setRenewalStatus(null);
 setError('');
 setPolling(false);
 setPollCount(0);
 setProcessing(false);
 setSuccess(false);
 setCardVerified(false);
 setTokenVerified(false);
 setCardError('');
 setTokenError('');
 loadOrderList();
 };

 // 如果已经成功，显示成功页面
 if (success && renewalStatus?.status === 'success') {
 return (
 <div className="min-h-screen bg-gradient-to-br from-accent-50 via-cream-50 to-cream-100 py-8">
 <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
 <div className="bg-cream-50 rounded-2xl shadow-xl p-6 sm:p-8">
 <div className="bg-green-50 border border-green-200 rounded-lg p-6">
 <div className="flex flex-col items-center">
 <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mb-4">
 <CheckCircle className="w-10 h-10 text-white" />
 </div>
 <p className="text-lg font-medium text-green-800">
 {t('renewal.messages.renewalSuccess')}
 </p>
 <p className="mt-2 text-sm text-green-700">
 {t('renewal.messages.renewalSuccessDesc')}
 </p>
 </div>
 </div>
 <div className="bg-cream-100 rounded-lg p-4 space-y-2 mt-6">
 <div className="flex justify-between">
 <span className="text-ink-600">{t('renewal.messages.taskId')}:</span>
 <span className="font-medium">{formData.processOrderId}</span>
 </div>
 <div className="flex justify-between">
 <span className="text-ink-600">{t('renewal.messages.cardCode')}:</span>
 <span className="font-medium">{formData.selectedCardCode}</span>
 </div>
 <div className="flex justify-between">
 <span className="text-ink-600">{t('renewal.messages.duration')}:</span>
 <span className="font-medium">{t('renewal.messages.oneMonth')}</span>
 </div>
 {renewalStatus.expiryDate && (
 <div className="flex justify-between">
 <span className="text-ink-600">{t('renewal.messages.expiryDate')}:</span>
 <span className="font-medium">{new Date(renewalStatus.expiryDate).toLocaleDateString()}</span>
 </div>
 )}
 </div>
 <div className="flex gap-4 mt-6">
 <button
 onClick={() => navigate('/')}
 className="flex-1 bg-cream-100 text-ink-700 py-3 rounded-lg font-semibold hover:bg-ink-100 transition-colors"
 >
 {t('renewal.buttons.backToHome')}
 </button>
 <button
 onClick={resetProcess}
 className="flex-1 bg-accent-500 text-white py-3 rounded-lg font-semibold hover:bg-accent-600 transition-colors"
 >
 {t('renewal.buttons.renewAnother')}
 </button>
 </div>
 </div>
 </div>
 </div>
 );
 }

 // 如果正在处理，显示处理页面
 if (processing) {
 return (
 <div className="min-h-screen bg-gradient-to-br from-accent-50 via-cream-50 to-cream-100 py-8">
 <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
 <div className="bg-cream-50 rounded-2xl shadow-xl p-6 sm:p-8">
 <div className="bg-accent-50 border border-accent-200 rounded-lg p-6">
 <div className="flex flex-col items-center">
 <div className="relative">
 <Loader2 className="w-16 h-16 text-accent-500 animate-spin" />
 <div className="absolute inset-0 flex items-center justify-center">
 <RefreshCw className="w-8 h-8 text-accent-600" />
 </div>
 </div>
 <p className="mt-4 text-lg font-medium text-accent-800">
 {t('renewal.messages.processingRenewal')}
 </p>
 <p className="mt-2 text-sm text-accent-600">
 {t('renewal.messages.taskId')}: {formData.processOrderId}
 </p>
 <p className="mt-1 text-xs text-accent-500">
 {t('renewal.messages.queryCount', { count: pollCount })}
 </p>
 </div>
 </div>
 <div className="bg-accent-50 border border-accent-200 rounded-lg p-4 mt-4">
 <div className="flex items-start gap-2">
 <AlertCircle className="w-5 h-5 text-accent-600 flex-shrink-0 mt-0.5" />
 <div>
 <p className="text-sm text-accent-800">
 {t('renewal.warnings.doNotClose')}
 </p>
 <p className="text-sm text-accent-700 mt-1">
 {t('renewal.warnings.contactSupport')}
 </p>
 </div>
 </div>
 </div>
 </div>
 </div>
 </div>
 );
 }

 return (
 <div className="min-h-screen bg-gradient-to-br from-accent-50 via-cream-50 to-cream-100 py-8">
 <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
 {/* 页面标题 */}
 <div className="text-center mb-8">
 <h1 className="text-3xl font-bold text-ink-900 mb-2">
 {t('renewal.page.title')}
 </h1>
 <p className="text-ink-600">
 {t('renewal.page.subtitle')}
 </p>
 </div>

 {/* 主要内容区域 */}
 <div className="bg-cream-50 rounded-2xl shadow-xl p-6 sm:p-8">
 {/* 错误提示 */}
 {error && (
 <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
 {/* 如果是登录相关错误，显示合并的提示和按钮 */}
 {error.includes('登录') ? (
 <div className="flex items-center justify-between">
 <div className="flex items-center gap-2">
 <AlertCircle className="w-5 h-5 text-red-600" />
 <span className="text-red-800">请先登录后再进行续费操作</span>
 </div>
 <button
 onClick={() => setIsLoginModalOpen(true)}
 className="inline-flex items-center gap-2 bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-lg font-medium transition-colors"
 >
 <User className="w-4 h-4" />
 立即登录
 </button>
 </div>
 ) : (
 <div className="flex items-center gap-2">
 <AlertCircle className="w-5 h-5 text-red-600" />
 <span className="text-red-800">{error}</span>
 </div>
 )}
 </div>
 )}

 {/* 表单内容 */}
 <div className="space-y-6">
 {/* 步骤1：选择订单号 */}
 <div className="border border-ink-200 rounded-lg p-4">
 <div className="flex items-center gap-2 mb-3">
 <div className="w-8 h-8 bg-accent-100 rounded-full flex items-center justify-center">
 <span className="text-accent-600 font-semibold">1</span>
 </div>
 <h3 className="text-lg font-semibold">{t('renewal.form.selectOrderTitle')}</h3>
 </div>
 <div>
 <label className="block text-sm font-medium text-ink-700 mb-2">
 {t('renewal.form.orderList')}
 </label>
 <select
 value={formData.selectedOrderId}
 onChange={(e) => setFormData({ ...formData, selectedOrderId: e.target.value })}
 className="w-full px-4 py-2 border border-ink-300 rounded-lg focus:ring-2 focus:ring-accent-500 focus:border-transparent"
 disabled={loading}
 >
 <option value="">{t('renewal.form.selectOrderPlaceholder')}</option>
 {orderList.map((order) => (
 <option key={order.id} value={order.id}>
 {order.orderNumber} - {order.description}
 </option>
 ))}
 </select>
 <p className="mt-2 text-sm text-ink-600">
 {t('renewal.form.orderSelectHint')}
 </p>
 </div>
 </div>

 {/* 步骤2：选择卡密 */}
 <div className={`border rounded-lg p-4 transition-all duration-200 ${
 !formData.selectedOrderId 
 ? 'opacity-50 border-ink-200' 
 : verifyingCard
 ? 'border-accent-400 bg-accent-50/50'
 : cardVerified
 ? 'border-green-400 bg-green-50/50'
 : cardError
 ? 'border-red-400 bg-red-50/50'
 : 'border-ink-200'
 }`}>
 <div className="flex items-center gap-2 mb-3">
 <div className="w-8 h-8 bg-accent-100 rounded-full flex items-center justify-center">
 <span className="text-accent-600 font-semibold">2</span>
 </div>
 <h3 className="text-lg font-semibold">{t('renewal.form.selectCardTitle')}</h3>
 {cardVerified && (
 <CheckCircle className="w-5 h-5 text-green-500" />
 )}
 {cardError && (
 <XCircle className="w-5 h-5 text-red-500" />
 )}
 </div>
 <div>
 <label className="block text-sm font-medium text-ink-700 mb-2">
 {t('renewal.form.cardList')}
 </label>
 <select
 value={formData.selectedCardCode}
 onChange={(e) => setFormData({ ...formData, selectedCardCode: e.target.value })}
 className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-accent-500 focus:border-transparent transition-all duration-200 ${
 cardVerified 
 ? 'border-green-300 bg-green-50' 
 : cardError
 ? 'border-red-300 bg-red-50'
 : 'border-ink-300'
 }`}
 disabled={loading || !formData.selectedOrderId}
 >
 <option value="">{t('renewal.form.selectCardPlaceholder')}</option>
 {cardList.map((card) => (
 <option key={card.code} value={card.code}>
 {card.code} - {card.description}
 </option>
 ))}
 </select>
 <p className="mt-2 text-sm text-ink-600">
 {t('renewal.form.cardSelectHint')}
 </p>
 
 {/* 卡密校验按钮 */}
 <div className="mt-3">
 <button
 onClick={verifyCard}
 disabled={verifyingCard || !formData.selectedCardCode}
 className={`w-full px-4 py-3 rounded-lg font-medium transition-all duration-200 flex items-center justify-center gap-2 ${
 verifyingCard 
 ? 'bg-accent-500 text-white cursor-not-allowed'
 : cardVerified
 ? 'bg-green-500 hover:bg-green-600 text-white'
 : cardError
 ? 'bg-red-500 hover:bg-red-600 text-white'
 : !formData.selectedCardCode
 ? 'bg-ink-200 text-ink-500 cursor-not-allowed'
 : 'bg-accent-500 hover:bg-accent-600 text-white'
 }`}
 >
 {verifyingCard ? (
 <>
 <Loader2 className="w-4 h-4 animate-spin" />
 {t('renewal.buttons.verifying')}
 </>
 ) : cardVerified ? (
 <>
 <CheckCircle className="w-4 h-4" />
 {t('renewal.buttons.cardVerified')}
 </>
 ) : cardError ? (
 <>
 <XCircle className="w-4 h-4" />
 {t('renewal.buttons.retryVerify')}
 </>
 ) : (
 <>
 <Search className="w-4 h-4" />
 {t('renewal.buttons.verifyCard')}
 </>
 )}
 </button>
 
 {/* 错误信息 */}
 {cardError && (
 <div className="mt-2 text-sm text-red-600 flex items-center gap-1">
 <AlertCircle className="w-4 h-4" />
 <span>{cardError}</span>
 </div>
 )}
 </div>
 </div>
 </div>

 {/* 步骤3：输入Token */}
 <div className={`border rounded-lg p-4 transition-all duration-200 ${
 !formData.selectedCardCode 
 ? 'opacity-50 border-ink-200' 
 : verifyingToken
 ? 'border-accent-400 bg-accent-50/50'
 : tokenVerified
 ? 'border-green-400 bg-green-50/50'
 : tokenError
 ? 'border-red-400 bg-red-50/50'
 : 'border-ink-200'
 }`}>
 <div className="flex items-center gap-2 mb-3">
 <div className="w-8 h-8 bg-accent-100 rounded-full flex items-center justify-center">
 <span className="text-accent-600 font-semibold">3</span>
 </div>
 <h3 className="text-lg font-semibold">{t('renewal.form.enterTokenTitle')}</h3>
 {tokenVerified && (
 <CheckCircle className="w-5 h-5 text-green-500" />
 )}
 {tokenError && (
 <XCircle className="w-5 h-5 text-red-500" />
 )}
 </div>
 <div>
 <label className="block text-sm font-medium text-ink-700 mb-2">
 {t('renewal.form.gptToken')}
 </label>
 <textarea
 rows="4"
 value={formData.token}
 onChange={(e) => setFormData({ ...formData, token: e.target.value })}
 className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-accent-500 focus:border-transparent font-mono text-sm transition-all duration-200 resize-vertical ${
 tokenVerified 
 ? 'border-green-300 bg-green-50' 
 : tokenError
 ? 'border-red-300 bg-red-50'
 : 'border-ink-300'
 }`}
 placeholder="粘贴accessToken或完整JSON对象..."
 disabled={!formData.selectedCardCode}
 />
 <div className="mt-2 p-3 bg-accent-50 border border-accent-200 rounded-lg">
 <div className="flex items-start gap-2">
 <AlertCircle className="w-5 h-5 text-accent-600 flex-shrink-0 mt-0.5" />
 <div className="text-sm">
 <p className="text-accent-800 font-medium mb-1">
 {t('renewal.form.tokenInstruction')}
 </p>
 <p className="text-accent-700 mb-2">
 {t('renewal.form.tokenStep1')}
 </p>
 <p className="text-accent-700 mb-2">
 {t('renewal.form.tokenStep2')}
 </p>
 <p className="text-accent-700 mb-2">
 <span className="font-medium">支持两种输入方式：</span><br/>
 1. 直接粘贴 accessToken 字符串<br/>
 2. 粘贴完整的JSON对象（自动提取accessToken）
 </p>
 <a
 href="https://chatgpt.com"
 target="_blank"
 rel="noopener noreferrer"
 className="inline-flex items-center gap-1 text-accent-600 hover:text-accent-800 underline"
 >
 {t('renewal.form.getTokenLink')}
 <ExternalLink className="w-4 h-4" />
 </a>
 <span className="mx-2">|</span>
 <a
 href="/token-tutorial"
 target="_blank"
 rel="noopener noreferrer"
 className="inline-flex items-center gap-1 text-accent-600 hover:text-accent-800 underline"
 >
 {t('renewal.form.tutorialLink')}
 <ExternalLink className="w-4 h-4" />
 </a>
 </div>
 </div>
 </div>
 
 {/* Token校验按钮 */}
 <div className="mt-3">
 <button
 onClick={verifyToken}
 disabled={verifyingToken || !formData.token}
 className={`w-full px-4 py-3 rounded-lg font-medium transition-all duration-200 flex items-center justify-center gap-2 ${
 verifyingToken 
 ? 'bg-accent-500 text-white cursor-not-allowed'
 : tokenVerified
 ? 'bg-green-500 hover:bg-green-600 text-white'
 : tokenError
 ? 'bg-red-500 hover:bg-red-600 text-white'
 : !formData.token
 ? 'bg-ink-200 text-ink-500 cursor-not-allowed'
 : 'bg-accent-500 hover:bg-accent-600 text-white'
 }`}
 >
 {verifyingToken ? (
 <>
 <Loader2 className="w-4 h-4 animate-spin" />
 {t('renewal.buttons.verifying')}
 </>
 ) : tokenVerified ? (
 <>
 <CheckCircle className="w-4 h-4" />
 {t('renewal.buttons.tokenVerified')}
 </>
 ) : tokenError ? (
 <>
 <XCircle className="w-4 h-4" />
 {t('renewal.buttons.retryVerify')}
 </>
 ) : (
 <>
 <Search className="w-4 h-4" />
 {t('renewal.buttons.verifyToken')}
 </>
 )}
 </button>
 
 {/* 错误信息 */}
 {tokenError && (
 <div className="mt-2 text-sm text-red-600 flex items-center gap-1">
 <AlertCircle className="w-4 h-4" />
 <span>{tokenError}</span>
 </div>
 )}
 </div>
 </div>
 </div>

 {/* 提交按钮 */}
 <button
 onClick={handleRenewal}
 disabled={loading || !formData.selectedOrderId || !cardVerified || !tokenVerified}
 className="w-full bg-accent-500 text-white py-3 rounded-lg font-semibold hover:bg-accent-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
 >
 {loading ? (
 <>
 <Loader2 className="w-5 h-5 animate-spin" />
 {t('renewal.buttons.loading')}
 </>
 ) : (
 <>
 <CreditCard className="w-5 h-5" />
 {t('renewal.buttons.confirmRenewal')}
 </>
 )}
 </button>
 </div>
 </div>

 {/* 帮助信息 */}
 <div className="mt-8 text-center text-sm text-ink-600">
 <p>{t('renewal.help.encounterProblem')}</p>
 <p className="mt-1">{t('renewal.help.supportEmail')}</p>
 </div>
 </div>
 
 {/* 登录弹窗 */}
 <LoginModal 
 isOpen={isLoginModalOpen}
 onClose={() => setIsLoginModalOpen(false)}
 onLogin={() => {
 setIsLoginModalOpen(false);
 // 登录成功后重新加载订单列表
 loadOrderList();
 // 清除之前的错误信息
 setError('');
 }}
 />
 </div>
 );
}